import React, { useState, useEffect } from "react";
import {
    Badge,
    IconButton,
    Menu,
    Typography,
    Box,
    useMediaQuery,
    useTheme,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import pusher from "../../tools/pusher";
import PreviewSound from "../../Assets/preview.mp3";

const ChatNotificationBell = () => {
    const theme = useTheme();
    const userData = JSON.parse(localStorage.getItem("user"));
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [chatNotifications, setChatNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        if (!userData?.user_id) return;

        const handleChatNotification = (data) => {
            const newNotification = {
                id: data.id || Date.now(),
                type: data.data.type ?? 'chat',
                data: data.data,
                timestamp: data.timestamp,
                isRead: false
            };

            setChatNotifications(prev => [newNotification, ...prev.slice(0, 9)]); // Keep only last 10
            setUnreadCount(prev => prev + 1);

            // Play sound or show browser notification
            playNotificationSound();
        };

        const channel = pusher.subscribe(`chat-user-notification.${userData.user_id}`);
        channel.bind("ChatUserNotification", handleChatNotification);

        return () => {
            channel.unbind("ChatUserNotification", handleChatNotification);
            pusher.unsubscribe(`chat-user-notification.${userData.user_id}`);
        };
    }, [userData?.user_id]);

    const playNotificationSound = () => {
        const audio = new Audio(PreviewSound);
        audio.play().catch(e => console.log('Audio play failed:', e));
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        // Mark all as read when opening
        setChatNotifications(prev => prev.map(noti => ({ ...noti, isRead: true })));
        setUnreadCount(0);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleChatClick = (notification) => {
        const type = notification.data.type || notification.type;
        if (type === 'chat'){
            const receiver = notification.data.participant
            const sender = notification.data.sender
            const userToSend = (receiver.id === userData.user_id && receiver.type === "User") ? sender : receiver
            // Navigate to chat
            // window.location.href = `/chats/individual/${userToSend.id}`;
            window.location.href = `/chats/individual/${userToSend.type.toLowerCase()}/${userToSend.id}`;
        }else if (type === 'group_message'){
            const group_id = notification.data.group_id
            window.location.href = `/chats/group/${group_id}`;
        }else if (type === 'channel_message'){
            const channel_id = notification.data.channel_id
            window.location.href = `/chats/channel/${channel_id}`;
        }else{
            const receiver = notification.data.participant
            const sender = notification.data.sender
            const userToSend = (receiver.id === userData.user_id && receiver.type === "User") ? sender : receiver
            // Navigate to chat
            // window.location.href = `/chats/individual/${userToSend.id}`;
            window.location.href = `/chats/individual/${userToSend.type.toLowerCase()}/${userToSend.id}`;
        }

        handleMenuClose();
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Box>
            <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                aria-label="chat notifications"
                size={"small"}
                style={{width:10}}
            >
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <ChatIcon style={{fill:'#4ea27d'}} fontSize={"small"} />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        width: isMobile ? "90vw" : 400,
                        maxHeight: "70vh",
                        borderRadius: "12px",
                        boxShadow: theme.shadows[4],
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderRadius: "12px 12px 0 0",
                    }}
                >
                    <Typography variant="h6">Chat Notifications</Typography>
                    <IconButton
                        onClick={handleMenuClose}
                        size="small"
                        sx={{ color: "inherit" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Chat Notification List */}
                <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
                    {chatNotifications.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="body2" color="textSecondary">
                                No new chat notifications
                            </Typography>
                        </Box>
                    ) : (
                        <List dense>
                            {chatNotifications.map((notification) => (
                                <ListItem
                                    key={notification.id}
                                    button
                                    onClick={() => handleChatClick(notification)}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        "&:hover": {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {!notification.isRead && (
                                            <FiberManualRecordIcon
                                                fontSize="small"
                                                color="primary"
                                                sx={{ width: 8, height: 8 }}
                                            />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={

                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {
                                                    notification.type === 'chat' ?
                                                       `New message from ${notification.data?.sender?.name}`  :
                                                        notification.type === 'group_message' ?
                                                        `New Group message from ${notification.data?.sender_name} in ${notification.data?.group_name} Group` :
                                                        `New Channel message from ${notification.data?.sender_name} in ${notification.data?.channel_name} Channel`
                                                }

                                            </Typography>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption" display="block">
                                                    {notification.data.message.snippet}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {formatTime(notification.timestamp)}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ my: 0 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Menu>
        </Box>
    );
};

export default ChatNotificationBell;